
import { useEffect, useState } from 'react';
import { supportGroupsApi, type SupportGroup, type UserSearchResult } from '@/shared/api';
import type { ApprovalAccessScope, TransitionInputSpec } from '@/entities/ticket';
import { AsyncUserSelect, Checkbox, DatePicker, Field, Input, Select, Textarea } from '@/shared/ui';
import { parseHours } from '@/pages/Sprints/work-units';
import { TemplatePicker } from '@/features/tickets/ui/TemplatePicker';
import type { TemplateContext } from '@/features/tickets/ui/TemplatePanel';
import { cn } from '@/shared/lib/cn';
import type { TransitionValues } from './transition-inputs';
import styles from '../TicketDetail.module.css';

export const TransitionFields: React.FC<{
  specs: TransitionInputSpec[];
  values: TransitionValues;
  onChange: (key: string, value: string) => void;
  userNames: Record<string, string>;
  onPickUser: (key: string, user: UserSearchResult) => void;
  scope: ApprovalAccessScope;
  onScopeChange: (next: ApprovalAccessScope) => void;
  templateContext?: TemplateContext | null;
}> = ({
  specs,
  values,
  onChange,
  userNames,
  onPickUser,
  scope,
  onScopeChange,
  templateContext,
}) => (
  <>
    {specs.map((spec) => {
      if (spec.kind === 'user_picker') {
        return (
          <div key={spec.key}>
            <span className={styles.subLabel}>{spec.label ?? 'Пользователь'}</span>
            <AsyncUserSelect
              placeholder="Поиск..."
              onSelect={(u: UserSearchResult) => onPickUser(spec.key, u)}
            />
            {userNames[spec.key] && (
              <p className={styles.actionHint}>Выбран: {userNames[spec.key]}</p>
            )}
          </div>
        );
      }
      if (spec.kind === 'text') {
        return (
          <div key={spec.key}>
            { }
            {templateContext && (
              <TemplatePicker
                context={templateContext}
                value={values[spec.key] ?? ''}
                onInsert={(next) => onChange(spec.key, next)}
              />
            )}
            <Field label={spec.label ?? spec.key} required={spec.required}>
              <Textarea
                id={`transition-${spec.key}`}
                rows={4}
                value={values[spec.key] ?? ''}
                onChange={(e) => onChange(spec.key, e.target.value)}
                placeholder={spec.key === 'resolution' ? 'Например: доступ выдан' : undefined}
              />
            </Field>
          </div>
        );
      }
      if (spec.kind === 'client_info') {
        return (
          <p key={spec.key} className={styles.actionHint}>
            Данные о вашей системе (браузер, ОС) будут переданы автоматически.
          </p>
        );
      }
      if (spec.kind === 'approval_access_scope') {
        return (
          <div key={spec.key}>
            <span className={cn(styles.subLabel, styles.subLabelFlushTop)}>
              {spec.label ?? 'Что видит согласующий'}
            </span>
            <AccessScopeChecks scope={scope} onChange={onScopeChange} />
          </div>
        );
      }
      if (spec.kind === 'support_group') {
        return (
          <SprintGroupSelect
            key={spec.key}
            label={spec.label ?? 'Группа разработки'}
            value={typeof values[spec.key] === 'string' ? (values[spec.key] as string) : ''}
            onChange={(next) => onChange(spec.key, next)}
          />
        );
      }
      if (spec.kind === 'work_minutes') {
        const raw = values[spec.key] ?? '';
        const invalid = parseHours(raw).kind === 'invalid';
        return (
          <Field
            key={spec.key}
            label={spec.label ?? 'Трудозатраты'}
            required={spec.required}
            hint={invalid ? undefined : 'В рабочих часах: 40, 2,5'}
            error={invalid ? 'Число часов, например 40 или 2,5' : undefined}
          >
            <Input
              id={`transition-${spec.key}`}
              inputMode="decimal"
              value={raw}
              onChange={(e) => onChange(spec.key, e.target.value)}
              placeholder="ч"
            />
          </Field>
        );
      }
      if (spec.kind === 'date') {
        return (
          <Field key={spec.key} label={spec.label ?? 'Дата'} required={spec.required}>
            <DatePicker
              id={`transition-${spec.key}`}
              value={values[spec.key] ?? ''}
              onChange={(next) => onChange(spec.key, next)}
            />
          </Field>
        );
      }
      return null;
    })}
  </>
);

const SprintGroupSelect: React.FC<{
  label: string;
  value: string;
  onChange: (next: string) => void;
}> = ({ label, value, onChange }) => {
  const [groups, setGroups] = useState<SupportGroup[]>([]);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    void supportGroupsApi
      .listBrief()
      .then((res) => alive && setGroups(res.data.filter((g) => g.sprintsEnabled)))
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, []);

  if (failed) {
    return (
      <p className={styles.actionHint}>
        Не удалось загрузить группы разработки. Задача уедет в группу, заданную в категории.
      </p>
    );
  }

  if (groups.length === 0) {
    return (
      <p className={styles.actionHint}>
        Ни у одной группы не включены спринты — задачу класть некуда. Включает администратор в
        карточке группы.
      </p>
    );
  }

  return (
    <Field
      label={label}
      floated
      hint="Используется, если группа не задана в категории или подкатегории заявки."
    >
      <Select
        value={value}
        onValueChange={onChange}
        options={[
          { value: '', label: '— по настройке категории —' },
          ...groups.map((g) => ({ value: g.id, label: g.name })),
        ]}
      />
    </Field>
  );
};

export const AccessScopeChecks: React.FC<{
  scope: ApprovalAccessScope;
  onChange: (next: ApprovalAccessScope) => void;
}> = ({ scope, onChange }) => (
  <div className={styles.checkRow}>
    <Checkbox
      checked={scope.canViewChat}
      onCheckedChange={(checked) => onChange({ ...scope, canViewChat: checked === true })}
      label="чат"
    />
    <Checkbox
      checked={scope.canViewAttachments}
      onCheckedChange={(checked) => onChange({ ...scope, canViewAttachments: checked === true })}
      label="вложения"
    />
  </div>
);
